import {
  Component,
  OnInit,
  ViewEncapsulation,
  inject,
  input,
} from '@angular/core';
import {
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormControl,
  FormsModule,
} from '@angular/forms';
import {
  MCCustomIdNumValidators,
  //MCCustomeIdNumValidators,
  MCCustomPhoneValidators,
  MCDialog,
  MCDialogButton,
  MCDialogFooter,
  MCDialogHeader,
  MCRegex,
} from '@maslul/common-services';
import { TooltipModule } from 'primeng/tooltip';
import {
  // MCRadioButtonGroup,
  MCFormFieldWrapperComponent,
  MCCityStreetListsComponent,
  MCInputTextComponent,
  McInfoComponent,
  MCRadioButtonGroup,
  MCAccessibilityLabelDirective,
  MCDialogComponent,
} from '@maslul/common-components';
import {
  distinctUntilChanged,
  Subscription,
  combineLatest,
  filter,
} from 'rxjs';
import { ApplicantDetails } from '../../../../../../models/applicant/applicant-details';
import { ValidationMessageData } from '../../../../../../resources/validation-massage';
import { TranslateService } from '@ngx-translate/core';
import { RequestApprovalTypeDetailsStore } from '../../../../../../services/store/approval-type-details-store';
import { ApprovalTypeData } from '../../../../../../services/approval-type/approvals-type-data';
import { GeneralStore } from '../../../../../../services/store/general';
import { CheckImporterService } from '../../../../../../services/authorities/events/common/check-importer.service';

@Component({
  selector: 'mi-applicant-details-fields',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    TooltipModule,
    MCFormFieldWrapperComponent,
    MCInputTextComponent,
    MCRadioButtonGroup,
    MCCityStreetListsComponent,
    McInfoComponent,
    MCAccessibilityLabelDirective,
    MCDialogComponent,
  ],
  templateUrl: './applicant-details-fields.html',
  styleUrl: './applicant-details-fields.scss',
  encapsulation: ViewEncapsulation.None,
})
export class ApplicantDetailsFieldsComponent implements OnInit {
  private translateService = inject(TranslateService);
  private requestApprovalTypeDetailsStore = inject(
    RequestApprovalTypeDetailsStore,
  );
  private checkImporterService = inject(CheckImporterService);

  readonly applicant = input<ApplicantDetails>();

  applicantDetailsForm!: FormGroup;
  private subscriptions: Subscription = new Subscription();
  showPackageNumberField: boolean = false;
  showIsYoursRadio: boolean = true;
  public customErrorMessages = ValidationMessageData.customErrorMessages;

  applicantText: any;
  isSentRequest: boolean = false;

  dialogProperties: MCDialog = new MCDialog();

  constructor(private generalStore: GeneralStore) {}

  ngOnInit(): void {
    this.initText();
    this.initForm();
    this.initSubscriptions();
    this.initDialog();
  }

  initText() {
    this.subscriptions.add(
      this.translateService
        .stream('applicantDetails')
        .subscribe((translation) => {
          this.applicantText = translation;
        }),
    );
  }

  initForm() {
    const applicant = this.applicant();
    this.applicantDetailsForm = new FormGroup({
      userId: new FormControl(applicant?.userId, [
        Validators.required,
        MCCustomIdNumValidators.idNum(),
      ]),
      firstName: new FormControl(applicant?.firstName, [
        Validators.required,
        Validators.pattern(MCRegex.freeText),
        Validators.maxLength(25),
      ]),
      lastName: new FormControl(applicant?.lastName, [
        Validators.required,
        Validators.pattern(MCRegex.freeText),
        Validators.maxLength(25),
      ]),
      mobile: new FormControl(applicant?.communicationDetails?.mobile, [
        Validators.required,
        MCCustomPhoneValidators.mobile(),
      ]),
      email: new FormControl(applicant?.communicationDetails?.email, [
        Validators.required,
        Validators.pattern(MCRegex.mail),
        Validators.maxLength(50),
      ]),
      city: new FormControl(applicant?.address?.city, {
        validators: [Validators.required],
        updateOn: 'change',
      }),
      street: new FormControl(applicant?.address?.street, {
        validators: [Validators.required],
        updateOn: 'change',
      }),
      homeNumber: new FormControl(applicant?.address?.homeNumber, [
        Validators.required,
        Validators.pattern(MCRegex.homeNumber),
        Validators.maxLength(6),
      ]),
      postalCode: new FormControl(applicant?.address?.postalCode, [
        Validators.pattern(MCRegex.postalCode),
        Validators.maxLength(7),
      ]),
      isPackageYours: new FormControl(applicant?.isPackageYours, {
        updateOn: 'change',
      }),
      packageOwnerGroup: new FormGroup({
        packageOwnerId: new FormControl(applicant?.packageOwnerId, [
          MCCustomIdNumValidators.idNum(),
        ]),
        packageOwnerFirstName: new FormControl(
          applicant?.packageOwnerFirstName,
          [Validators.pattern(MCRegex.freeText), Validators.maxLength(25)],
        ),
        packageOwnerLastName: new FormControl(applicant?.packageOwnerLastName, [
          Validators.pattern(MCRegex.freeText),
          Validators.maxLength(25),
        ]),
        packageOwnerMobile: new FormControl(
          applicant?.packageOwnerCommunicationDetails?.mobile,
          [MCCustomPhoneValidators.mobile()],
        ),
        packageOwnerEmail: new FormControl(
          applicant?.packageOwnerCommunicationDetails?.email,
          [Validators.pattern(MCRegex.mail), Validators.maxLength(50)],
        ),
      }),
    });
    this.viewIsPackageYours();
    this.viewPackageOwner(applicant?.isPackageYours);
  }

  get isInvalidAndTouchedUserId(): boolean {
    const control: FormControl = this.getFormControl('userId');
    return (control?.invalid && (control?.touched || control?.dirty)) || false;
  }

  get isInvalidAndTouchedPackageOwnerId(): boolean {
    const packageOwnerGroup = this.applicantDetailsForm.get(
      'packageOwnerGroup',
    ) as FormGroup;

    const control: FormControl = packageOwnerGroup.get(
      'packageOwnerId',
    ) as FormControl;
    return (control?.invalid && (control?.touched || control?.dirty)) || false;
  }

  getFormControl(key: string): FormControl {
    const control = this.applicantDetailsForm.get(key);
    if (control instanceof FormControl) {
      return control;
    }
    throw new Error(`קונטרול עם מפתח '${key}' אינו FormControl או אינו קיים.`);
  }

  getFormGroup(key: string): FormGroup {
    const control = this.applicantDetailsForm.get(key);
    if (control instanceof FormGroup) {
      return control;
    }
    throw new Error(`קונטרול עם מפתח '${key}' אינו FormControl או אינו קיים.`);
  }

  viewIsPackageYours() {
    const isPackageYoursControl =
      this.applicantDetailsForm.get('isPackageYours');
    const currentApprovalTypeId: string =
      this.requestApprovalTypeDetailsStore.getSnapshot().approvalTypeId;
    if (
      ApprovalTypeData.isPackageYoursFieldDisplay.includes(
        +currentApprovalTypeId,
      )
    ) {
      this.showIsYoursRadio = false;
      isPackageYoursControl?.setValue('yes');
    } else {
      isPackageYoursControl?.addValidators(Validators.required);
    }
  }

  viewPackageOwner(isPackageYours: string | undefined) {
    const packageOwnerGroup = this.applicantDetailsForm.get(
      'packageOwnerGroup',
    ) as FormGroup;
    if (isPackageYours === 'no') {
      this.showPackageNumberField = true;
      packageOwnerGroup.enable();
      packageOwnerGroup
        .get('packageOwnerId')!
        .addValidators(Validators.required);
      packageOwnerGroup
        .get('packageOwnerFirstName')!
        .addValidators(Validators.required);
      packageOwnerGroup
        .get('packageOwnerLastName')!
        .addValidators(Validators.required);
      packageOwnerGroup
        .get('packageOwnerMobile')!
        .addValidators(Validators.required);
      packageOwnerGroup
        .get('packageOwnerEmail')!
        .addValidators(Validators.required);
    } else {
      this.showPackageNumberField = false;
      packageOwnerGroup.disable();
      packageOwnerGroup.reset();
      packageOwnerGroup
        .get('packageOwnerId')!
        .removeValidators(Validators.required);
      packageOwnerGroup
        .get('packageOwnerFirstName')!
        .removeValidators(Validators.required);
      packageOwnerGroup
        .get('packageOwnerLastName')!
        .removeValidators(Validators.required);
      packageOwnerGroup
        .get('packageOwnerMobile')!
        .removeValidators(Validators.required);
      packageOwnerGroup
        .get('packageOwnerEmail')!
        .removeValidators(Validators.required);
    }
  }

  initSubscriptions() {
    this.subscriptions.add(
      this.generalStore
        .getIsSent()
        .subscribe((isSent) => (this.isSentRequest = isSent)),
    );

    this.subscriptions.add(
      this.applicantDetailsForm
        .get('isPackageYours')
        ?.valueChanges.pipe(distinctUntilChanged())
        .subscribe((isPackageYours) => {
          this.viewPackageOwner(isPackageYours);

          if (isPackageYours === 'no') {
            const userIdControl = this.applicantDetailsForm.get(
              'userId',
            ) as FormControl;
            this.handleCheckImporterValid(userIdControl);
          }
        }),
    );

    const userIdControl = this.applicantDetailsForm.get('userId');
    const packageOwnerIdControl = this.applicantDetailsForm.get(
      'packageOwnerGroup.packageOwnerId',
    );
    const isPackageYoursControl =
      this.applicantDetailsForm.get('isPackageYours');

    if (userIdControl && packageOwnerIdControl && isPackageYoursControl) {
      this.subscriptions.add(
        combineLatest([
          userIdControl.valueChanges.pipe(distinctUntilChanged()),
          packageOwnerIdControl.valueChanges.pipe(distinctUntilChanged()),
          isPackageYoursControl.valueChanges.pipe(distinctUntilChanged()),
        ])
          .pipe(
            filter(([userId, packageOwnerId, isPackageYours]) => {
              return (
                (isPackageYours === 'yes' && !!userId) ||
                (isPackageYours === 'no' && !!packageOwnerId)
              );
            }),
          )
          .subscribe(([userId, packageOwnerId, isPackageYours]) => {
            const importerId =
              isPackageYours === 'yes' ? userId : packageOwnerId;
            const control =
              isPackageYours === 'yes' ? userIdControl : packageOwnerIdControl;
            if (importerId) {
              this.checkImporter(
                { importerId: importerId },
                control as FormControl,
              );
            }
          }),
      );
    }

    const cityControl = this.applicantDetailsForm.get('city') as FormControl;

    this.subscriptions.add(
      cityControl?.valueChanges
        .pipe(distinctUntilChanged())
        .subscribe((city) => {
          if (city) {
            this.checkImporter(
              { importerSettlementId: city.value },
              cityControl,
            );
          }
        }),
    );
  }

  public isFormValid(): boolean {
    this.applicantDetailsForm.updateValueAndValidity();
    return this.applicantDetailsForm.valid;
  }

  private checkImporter(args: any, control: FormControl): void {
    const extendedArgs = {
      ...args,
      invalidMsg: this.applicantText.checkImporter.dialog.invalidMsg,
    };

    const checkResult$ =
      this.checkImporterService.checkImporterByApprovalType(extendedArgs);

    if (checkResult$) {
      this.subscriptions.add(
        checkResult$.subscribe({
          next: (checkImporterResult) => {
            if (!checkImporterResult.isValid) {
              this.showDialog(checkImporterResult.message);
              this.handleCheckImporterInvalid(control);
            } else {
              this.handleCheckImporterValid(control);
            }
          },
          error: (error) => {
            console.error('שגיאה בבדיקת יבואן:', error);
          },
        }),
      );
    }
  }

  private handleCheckImporterInvalid(control: FormControl): void {
    control.setErrors({ checkImporterInvalid: true });
    control.markAsTouched();
  }

  private handleCheckImporterValid(control: FormControl): void {
    const errors = control.errors;
    if (errors && errors['checkImporterInvalid']) {
      delete errors['checkImporterInvalid'];
      control.setErrors(Object.keys(errors).length > 0 ? errors : null);
    }
  }

  initDialog() {
    //todo  dialogProperties.breakpoints to enum
    this.dialogProperties.breakpoints = {
      '9000px': '40vw',
      '640px': '80vw', // ברוחב 640px ומטה, רוחב הדיאלוג יהיה 80vw
    };
    this.dialogProperties.id = 'mi-check-importer-dialog';
  }

  showDialog(message: string) {
    this.dialogProperties.visible = true;

    this.dialogProperties.header = new MCDialogHeader();
    this.dialogProperties.header.closeButton = new MCDialogButton();
    this.dialogProperties.header.closeButton.label = 'x';
    this.dialogProperties.header.closeButton.event = () => {
      this.dialogProperties.visible = false;
    };

    this.dialogProperties.footer = new MCDialogFooter();
    this.dialogProperties.footer.button = new MCDialogButton();
    this.dialogProperties.footer.button.label =
      this.applicantText.checkImporter.dialog.okBtn;
    this.dialogProperties.footer.button.event = () => {
      this.dialogProperties.visible = false;
    };

    this.dialogProperties.contentText = message;
  }
}
